import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { getPlanCollection } from '../getCollections'
import { normaliseString } from './useHarmonyPlanContext.planMutations.shared'

const logger = useRhythmicLogger('useHarmonyPlanContext:updatePlan')

export interface UpdateHarmonyPlanPayload {
  title?: string
  ariaStatus?: string | null
  ariaPlan?: string | null
  currentAction?: string | null
  currentActionStartedAt?: string | Date | null
}

export interface UpdateHarmonyPlanContext {
  planId: string
  teamId: string
  workspaceId: string
}

function buildUpdateBody(payload: UpdateHarmonyPlanPayload) {
  const body: Record<string, unknown> = {}

  const title = normaliseString(payload.title)
  if (title !== undefined) body.title = title

  if (payload.ariaStatus !== undefined) body.ariaStatus = payload.ariaStatus

  if (payload.ariaPlan !== undefined) body.ariaPlan = payload.ariaPlan

  if (payload.currentAction !== undefined) body.currentAction = payload.currentAction

  if (payload.currentActionStartedAt !== undefined) {
    const value = payload.currentActionStartedAt
    if (value === null) {
      body.currentActionStartedAt = null
    } else if (value instanceof Date) {
      body.currentActionStartedAt = value.toISOString()
    } else {
      body.currentActionStartedAt = value
    }
  }

  return body
}

function validateParams(
  payload: UpdateHarmonyPlanPayload,
  context: UpdateHarmonyPlanContext,
  updateBody: Record<string, unknown>,
) {
  const { planId, teamId, workspaceId } = context
  if (!planId) {
    const error = new Error('Missing harmony plan identifier in update context')
    logger.warn('update harmony plan rejected: missing planId', { payload })
    throw error
  }

  if (!teamId) {
    const error = new Error('Missing team identifier in update context')
    logger.warn('update harmony plan rejected: missing teamId', { planId })
    throw error
  }

  if (!workspaceId) {
    const error = new Error('Missing workspace identifier in update context')
    logger.warn('update harmony plan rejected: missing workspaceId', {
      planId,
      teamId,
    })
    throw error
  }

  if (Object.keys(updateBody).length === 0) {
    const error = new Error('At least one field must be provided to update the harmony plan')
    logger.warn('update harmony plan rejected: no changes supplied', {
      planId,
    })
    throw error
  }

  return { planId, teamId, workspaceId }
}

export function handleUpdateHarmonyPlan(
  payload: UpdateHarmonyPlanPayload,
  context: UpdateHarmonyPlanContext,
): Transaction {
  logger.debug('Updating harmony plan', { payload, context })
  const body = buildUpdateBody(payload)

  const { planId, teamId, workspaceId } = validateParams(payload, context, body)
  logger.debug('Validated parameters for updating harmony plan', {
    planId,
    teamId,
    workspaceId,
    body,
  })
  try {
    const planCollection = getPlanCollection(workspaceId)
    const transaction = planCollection.update(planId, (draft) => {
      logger.debug('Applying update to harmony plan draft', { draft, body })
      Object.assign(draft, body)
    })
    logger.debug('Harmony plan updated', {
      planId,
      teamId,
      workspaceId,
      transaction,
    })
    return transaction
  } catch (error) {
    logger.error('Failed to update harmony plan', { planId, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to update harmony plan')
  }
}
